import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import Button from '@ui/Button';
import SafeImage from '@ui/SafeImage';
import Spinner from '@ui/Spinner';
import {
  deleteComment,
  getBlogComments,
  postComment,
} from '@util/blog';
import {
  BlogCommentDocument,
  blogCommentDocumentSchema,
} from '@util/types/firestore/blog';
import { useAuth } from 'context/AuthContext';
import { useState } from 'react';

const BlogCommentSection = ({ blogId }: { blogId: string }) => {

  const { data, isLoading } = useQuery({
    queryKey: ['blogComments', blogId, null],
    queryFn: () => getBlogComments({ blogId, parentId: null }),
  });

  const comments = data?.results ?? [];
  const commentCount = comments.length;

  return (
    <div className="m-auto mt-[4rem]">
      <div className="flex">
        <ChatsIcon />
        <h3 className="ml-[1.6rem] text-[2.4rem] font-semibold">
          Comments ({commentCount || 0})
        </h3>
      </div>

      <CommentInput commentType="comment" blogId={blogId} parentId={null} />

      <div className="mt-[2rem]">
        {isLoading ? (
          <div className="m-auto h-[4rem] w-[4rem]">
            <Spinner />
          </div>
        ) : (
          comments.map((c, i: number) => (
            <BlogComment key={i} comment={c} blogId={blogId} depth={0} />
          ))
        )}
      </div>
    </div>
  )

};

export default BlogCommentSection;

type BlogCommentProps = {
  comment: BlogCommentDocument;
  blogId: string;
  depth: number;
};

const BlogComment = ({ comment, blogId, depth }: BlogCommentProps) => {
  const [reply, setReply] = useState(false);
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const { data } = useQuery({
    queryKey: ['blogComments', blogId, comment.comment_id],
    queryFn: () => getBlogComments({ blogId, parentId: comment.comment_id }),
  });
  const replies = data?.results ?? [];
  const deleteCommentMutation = useMutation({
    mutationFn: deleteComment,
    onSuccess: () => {
      queryClient.invalidateQueries([
        'blogComments',
        blogId,
        comment.parent_id,
      ]);
    },
  });
  const handleDeleteClick = () => {
    deleteCommentMutation.mutate({ commentId: comment.comment_id });
  };
  const hideReplies = depth > 3;
  return (
    <>
      <hr className="mt-[1.6rem]" />
      <div
        className={`flex items-start gap-[1.6rem] pt-[1.6rem] ${
          depth === 0 ? 'overflow-scroll' : ''
        }`}
      >
        <SafeImage
          alt={'pfp image'}
          src={comment.pfp_url}
          width={44}
          height={44}
          key={comment.pfp_url}
          className="rounded-full"
        />
        <div className="grow">
          {/* title */}
          <p className="font-semibold">
            {comment.author_name}
            <span className="ml-[.8rem] text-[1.3rem] font-normal text-brand-gray">
              {new Date(comment.timestamp).toLocaleDateString()}
            </span>
          </p>
          {/* comment */}
          <p className="text-brand-black">{comment.comment}</p>
          {/* buttons */}
          <div>
            <button
              className="mt-[.8rem] font-semibold text-brand-secondary"
              onClick={() => setReply(!reply)}
            >
              Reply
            </button>
            {!!user && comment.author_id === user.uid && (
              <button
                className="ml-[1.6rem] mt-[.8rem] font-semibold text-brand-red"
                onClick={handleDeleteClick}
              >
                Delete
              </button>
            )}
          </div>

          {/* user reply */}
          {reply && (
            <CommentInput
              placeholder={`Reply to ${comment.author_name}`}
              commentType="reply"
              blogId={blogId}
              parentId={comment.comment_id}
              onCommentSuccess={() => setReply(false)}
            />
          )}

          {/* threads */}
          {hideReplies && replies.length > 0 ? (
            <button className="mt-4 w-full font-medium text-brand-secondary">
              show more
            </button>
          ) : (
            replies?.map((reply) => (
              <BlogComment
                key={reply.comment_id}
                comment={reply}
                blogId={blogId}
                depth={depth + 1}
              />
            ))
          )}
        </div>
      </div>
    </>
  );
};

const CommentInput = ({
  placeholder = 'Leave a comment',
  commentType,
  blogId,
  parentId = null,
  onCommentSuccess,
}: {
  placeholder?: string;
  commentType: 'reply' | 'comment';
  blogId: string; // blogId or commentId
  parentId: string | null;
  onCommentSuccess?: () => void;
}) => {
  const queryClient = useQueryClient();
  const { userDoc, user } = useAuth();
  const [input, setInput] = useState('');
  const postCommentMutation = useMutation({
    mutationFn: postComment,
    onSuccess: async () => {
      await queryClient.invalidateQueries(['blogComments', blogId]);
      setInput('');
      onCommentSuccess?.();
    },
    onError: (err) => {
      console.log(err);
    },
  });

  const handlePostClick = () => {
    if (!input || !userDoc) return;
    const doc = blogCommentDocumentSchema.parse({
      author_id: userDoc.uid,
      author_name: userDoc.name,
      blog_id: blogId,
      comment: input,
      comment_id: '',
      pfp_url: userDoc.photo,
      timestamp: Date.now(),
      reply_ids: [],
      parent_id: parentId,
    });
    postCommentMutation.mutate(doc);
  };
  return (
    <>
      <hr className="mt-[2rem]" />
      {/* logged out */}
      {!user && (
        <div className="m-auto flex flex-col items-center gap-[1.6rem] p-[2.4rem]">
          <PadlockIcon />
          <p>You must login first to leave a comment</p>
          <div className="max-w-[14rem]">
            <Button type="secondary" href="/login" text="Login Now" />
          </div>
        </div>
      )}
      {/* logged in */}
      {!!userDoc && (
        <div className="mt-[2.4rem] flex flex-col gap-[1.6rem]">
          <div className="flex items-start gap-[1.6rem]">
            <SafeImage
              src={userDoc.photo}
              alt="pfp image"
              unoptimized
              height={44}
              width={44}
              className="hidden rounded-full sm:block"
            />
            <textarea
              onChange={(e) => setInput(e.target.value)}
              placeholder={placeholder}
              value={input}
              className="focus:outline-primary block min-h-[10rem] w-full resize-none rounded-[1.6rem] bg-brand-darker-white p-[1.6rem]"
            />
          </div>
          <div className="w-fit self-end">
            <Button
              loading={postCommentMutation.isLoading}
              disabled={!input}
              type="secondary"
              text={commentType === 'comment' ? 'Post Comment' : 'Post Reply'}
              onClick={handlePostClick}
            />
          </div>
        </div>
      )}
    </>
  );
};

const ChatsIcon = () => {
  return (
    <svg
      width="32"
      height="32"
      viewBox="0 0 32 32"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        d="M8.95 18L4 22V6C4 5.73478 4.10536 5.48043 4.29289 5.29289C4.48043 5.10536 4.73478 5 5 5H21C21.2652 5 21.5196 5.10536 21.7071 5.29289C21.8946 5.48043 22 5.73478 22 6V17C22 17.2652 21.8946 17.5196 21.7071 17.7071C21.5196 17.8946 21.2652 18 21 18H8.95Z"
        stroke="#808080"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M10 18V23C10 23.2652 10.1054 23.5196 10.2929 23.7071C10.4804 23.8946 10.7348 24 11 24H23.05L28 28V12C28 11.7348 27.8946 11.4804 27.7071 11.2929C27.5196 11.1054 27.2652 11 27 11H22"
        stroke="#808080"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
};

const PadlockIcon = () => {
  return (
    <svg
      width="60"
      height="60"
      viewBox="0 0 60 60"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        d="M22.6327 27.5789V19.8844C22.6327 17.9303 23.409 16.0563 24.7907 14.6745C26.1724 13.2928 28.0465 12.5166 30.0005 12.5166C31.9546 12.5166 33.8286 13.2928 35.2104 14.6745C36.5921 16.0563 37.3684 17.9303 37.3684 19.8844V27.5789H41.4465V19.8844C41.4465 16.8485 40.2405 13.9369 38.0938 11.7902C35.9471 9.64351 33.0355 8.4375 29.9996 8.4375C26.9637 8.4375 24.0522 9.64351 21.9054 11.7902C19.7587 13.9369 18.5527 16.8485 18.5527 19.8844V27.5789H22.6327Z"
        fill="#242E5F"
      />
      <path
        d="M43.2558 26.7275H16.7442C15.7032 26.7275 14.8594 27.5714 14.8594 28.6124V46.8655C14.8594 47.9065 15.7032 48.7504 16.7442 48.7504H43.2558C44.2968 48.7504 45.1406 47.9065 45.1406 46.8655V28.6124C45.1406 27.5714 44.2968 26.7275 43.2558 26.7275Z"
        fill="#C75008"
      />
    </svg>
  );
};
